import React, {
    useState, useEffect, useCallback, useRef
} from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import LayoutCard from 'components/FrictionlessCheckout/LayoutCard/LayoutCard';
import ShipAddressSection from 'components/FrictionlessCheckout/DeliverTo/ShipAddress/Section';
import ShipAddressSectionReleaseThree from 'components/FrictionlessCheckout/DeliverToReleaseThree/ShipAddress/Section';
import AddressDisplay from 'components/FrictionlessCheckout/DeliverTo/AddressDisplay';
import AddressDisplayReleaseThree from 'components/FrictionlessCheckout/DeliverToReleaseThree/AddressDisplay';
import CheckoutItemsList from 'components/FrictionlessCheckout/CheckoutItemsList';
import { Box, Divider, Text } from 'components/ui';
import dateUtils from 'utils/Date';
import BasketConstants from 'constants/Basket';
import { SECTION_NAMES, FC_CHECKOUT_SECTIONS, FC_CHECKOUT_PATH } from 'constants/frictionlessCheckout';
import FrictionlessCheckoutBindings from 'analytics/bindingMethods/pages/FrictionlessCheckout/FrictionlessCheckoutBindings';
import FrictionlessUtils from 'utils/FrictionlessCheckout';
import anaConsts from 'analytics/constants';

const { BasketType } = BasketConstants;
const isFCReleaseThreeEnabled = FrictionlessUtils.isFCReleaseThreeEnabled();

const ShippingAddressInformation = ({ address, onChangeClick }) => {
    const ComponentToRender = FrictionlessUtils.isFCReleaseThreeEnabled() ? AddressDisplayReleaseThree : AddressDisplay;

    return (
        <Box paddingX={[4, 4, 5]}>
            <ComponentToRender
                address={address}
                showEdit={true}
                onChangeClick={onChangeClick}
                isGiftCardSection={true}
            />
        </Box>
    );
};

const GiftCardDeliverySection = props => {
    const {
        isZeroCheckout,
        addressList,
        isNewUserFlow,
        shippingGroup,
        localization,
        isGuestCheckout,
        orderHasPhysicalGiftCard,
        sectionNumber,
        getAddressBook,
        addressByPreferredZipCode,
        setActiveSection,
        sectionName,
        sectionLevelError,
        isEditViewFromURL,
        goToURL
    } = props;

    const [isEditMode, setIsEditMode] = useState(false);
    const [isEditByChangeLink, setIsEditByChangeLink] = useState(false);
    const prevRef = useRef();

    useEffect(() => {
        getAddressBook(false);
    }, [getAddressBook]);

    useEffect(() => {
        if (prevRef.current && !isEditViewFromURL) {
            setIsEditByChangeLink(false);
            setIsEditMode(false);
        }

        prevRef.current = isEditViewFromURL;
    }, [isEditViewFromURL]);

    useEffect(() => {
        if (addressByPreferredZipCode > 1) {
            setEditChangeLink(true);
        }
    }, [addressByPreferredZipCode]);

    const setEditChangeLink = useCallback(editMode => {
        setIsEditMode(editMode);
        setIsEditByChangeLink(editMode);

        editMode && FrictionlessCheckoutBindings.setChangeLinkAnalytics(anaConsts.PAGE_DETAIL.GIFT_CARD_SHIPPING, true);

        setActiveSection(sectionName);
        isFCReleaseThreeEnabled &&
            goToURL({ path: editMode ? `${FC_CHECKOUT_PATH}/${FC_CHECKOUT_SECTIONS.GIFT_CARD_DELIVERY.path}` : FC_CHECKOUT_PATH });
    }, []);

    useEffect(() => {
        if (!isFCReleaseThreeEnabled) {
            return () => {};
        }

        const giftCardEditPath = `${FC_CHECKOUT_PATH}/${FC_CHECKOUT_SECTIONS.GIFT_CARD_DELIVERY.path}`;
        const handlePopState = () => {
            if (window.location.pathname !== giftCardEditPath) {
                setIsEditByChangeLink(false);
                setIsEditMode(false);
            }
        };

        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('popstate', handlePopState);
        };
    }, [isFCReleaseThreeEnabled]);

    const renderCardBody = useCallback(() => {
        const { address, isComplete, shippingGroupId, shippingMethod } = shippingGroup;
        const { promiseDate, promiseDateLabel, shippingMethodType } = shippingMethod;
        const { free } = localization;

        const ShipAddressSectionToRender = FrictionlessUtils.isFCReleaseThreeEnabled() ? ShipAddressSectionReleaseThree : ShipAddressSection;

        return (
            <>
                {address && isComplete && !isEditByChangeLink ? (
                    <>
                        <ShippingAddressInformation
                            address={address}
                            onChangeClick={setEditChangeLink}
                        />
                        <Box marginX={[4, 4, 5]}>
                            <Divider marginY={4} />
                            <Text
                                is='p'
                                fontWeight='bold'
                                children={`${free} ${shippingMethodType}`}
                            />
                            {promiseDate && (
                                <Text
                                    is='p'
                                    color='green'
                                    fontWeight='bold'
                                    children={`${promiseDateLabel} ${dateUtils.getPromiseDate(promiseDate)} `}
                                />
                            )}
                            <Divider marginY={4} />
                            <CheckoutItemsList
                                shouldDisplayTitle={true}
                                giftCardsOnly
                                basketType={BasketType.Standard}
                            />
                        </Box>
                    </>
                ) : (
                    <Box paddingX={[4, 4, 5]}>
                        <ShipAddressSectionToRender
                            isNewUserFlow={isNewUserFlow}
                            shippingAddress={addressList?.length && address}
                            profileAddresses={addressList || []}
                            shippingGroupId={shippingGroupId}
                            isComplete={isComplete}
                            isZeroCheckout={isZeroCheckout}
                            isGuestCheckout={isGuestCheckout}
                            shippingMethod={shippingMethod}
                            setAccessPoint={setEditChangeLink}
                            orderHasPhysicalGiftCard={orderHasPhysicalGiftCard}
                            isGiftCardSection={true}
                            showTaxExemptAddress
                            {...(FrictionlessUtils.isFCReleaseThreeEnabled() && {
                                sectionName: SECTION_NAMES.GIFT_CARD_DELIVERY,
                                currentAddressListLength: addressList?.length || 0
                            })}
                        />
                    </Box>
                )}
            </>
        );
    }, [
        shippingGroup,
        localization,
        isEditByChangeLink,
        isNewUserFlow,
        addressList,
        isZeroCheckout,
        isGuestCheckout,
        orderHasPhysicalGiftCard,
        setEditChangeLink,
        isEditViewFromURL
    ]);

    const title = localization.giftCardTitle;
    const subTitle = localization.giftCardSubtitle;
    const shouldShowSubtitle = (isEditMode || isNewUserFlow || (!isGuestCheckout && !addressList.length)) && isFCReleaseThreeEnabled;

    return (
        <LayoutCard
            sectionInfo={{
                sectionNumber: !sectionLevelError && sectionNumber,
                title,
                hasDivider: true,
                isChangePermitted: false,
                sectionLevelError,
                sectionName: SECTION_NAMES.GIFT_CARD_DELIVERY,
                subTitle: shouldShowSubtitle ? subTitle : ''
            }}
            marginTop={[4, 4, 5]}
            hasPaddingForChildren={false}
            isEditMode={
                (isEditMode && !isFCReleaseThreeEnabled) ||
                (isEditViewFromURL && isFCReleaseThreeEnabled) ||
                isNewUserFlow ||
                (!isGuestCheckout && !addressList.length)
            }
            buttonCustomStyle={styles}
            isNewUserFlow={isNewUserFlow}
            ariaLabel={localization.giftCardTitle}
            role='region'
        >
            {renderCardBody()}
        </LayoutCard>
    );
};

const styles = {
    button: {
        width: '48%'
    }
};

export default wrapFunctionalComponent(GiftCardDeliverySection, 'GiftCardDeliverySection');
